import React, { useEffect, useState } from "react";
import { getPlane } from "../../Services/plansApi";
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Info as InfoIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  Stars as StarsIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { themeColors } from "../../theme/colors";

const BrokerPlanesPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planes, setPlanes] = useState([]);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // features dialog
  const [featuresDialogOpen, setFeaturesDialogOpen] = useState(false);
  const [dialogFeatures, setDialogFeatures] = useState([]);

  const fetchPlanes = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const res = await getPlane(null, token);
      console.log("Fetched planes:", res);
      // الدالة قد تُعيد أشكال مختلفة؛ نحاول الوصول إلى المصفوفة بمرونة
      const planesList =
        // أضفنا هنا فحص res.data.data.* لأن الاستجابة من Axios تكون nested
        res?.data?.data?.palnes ||
        res?.data?.data?.planes ||
        res?.data?.palnes ||
        res?.data?.planes ||
        res?.palnes ||
        res?.planes ||
        res?.data ||
        res ||
        [];
      setPlanes(Array.isArray(planesList) ? planesList : []);
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "حدث خطأ أثناء جلب الباقات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanes();
  }, []);

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  };

  const openFeaturesDialog = (features = []) => {
    setDialogFeatures(Array.isArray(features) ? features : []);
    setFeaturesDialogOpen(true);
  };
  const closeFeaturesDialog = () => {
    setFeaturesDialogOpen(false);
    setDialogFeatures([]);
  };

  const paged = planes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Card
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${themeColors.secondary.main} 0%, ${themeColors.secondary.dark} 100%)`,
            color: "white",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "rgba(255,255,255,0.25)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CategoryIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                باقات الشركة
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                عرض جميع الباقات والخطط المتاحة
              </Typography>
            </Box>
          </Box>
        </Card>

        {loading && (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={60} sx={{ color: themeColors.secondary.main }} />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
            }}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && planes.length === 0 && (
          <Card
            elevation={0}
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 3,
              border: `2px dashed ${themeColors.border.main}`,
            }}
          >
            <CategoryIcon sx={{ fontSize: 80, color: themeColors.text.light, mb: 2 }} />
            <Typography variant="h6" sx={{ color: themeColors.text.secondary, mb: 1 }}>
              لا توجد باقات حالياً
            </Typography>
            <Typography variant="body2" sx={{ color: themeColors.text.secondary }}>
              سيتم عرض الباقات المتاحة هنا
            </Typography>
          </Card>
        )}

        {!loading && !error && planes.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${themeColors.border.light}`,
            }}
          >
            <TableContainer>
              <Table sx={{ direction: "rtl" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.secondary.main}`,
                      }}
                    >
                      معرّف
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.secondary.main}`,
                      }}
                    >
                      اسم الباقة
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.secondary.main}`,
                      }}
                    >
                      الوصف
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.secondary.main}`,
                      }}
                    >
                      الأسعار
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.secondary.main}`,
                      }}
                    >
                      الحالة
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.secondary.main}`,
                      }}
                    >
                      الميزات
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.secondary.main}`,
                      }}
                    >
                      تاريخ الإنشاء
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((p) => (
                    <TableRow
                      key={p.id}
                      sx={{
                        "&:hover": { bgcolor: themeColors.background.hover },
                        "&:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      <TableCell align="right">
                        <Chip
                          label={`#${p.id}`}
                          size="small"
                          sx={{
                            bgcolor: `${themeColors.secondary.main}15`,
                            color: themeColors.secondary.main,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CategoryIcon sx={{ color: themeColors.secondary.main, fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {p.plane_name ?? "-"}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                          {p.desc ?? "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Typography variant="caption" display="block" sx={{ color: themeColors.text.secondary }}>
                            💰 شهري: <strong>{p.monthly_price != null ? `${p.monthly_price} جنية` : "-"}</strong>
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ color: themeColors.text.secondary }}>
                            💰 سنوي: <strong>{p.yearly_price != null ? `${p.yearly_price} جنية` : "-"}</strong>
                          </Typography>
                          {p.descount != null && p.descount > 0 && (
                            <Chip
                              label={`خصم ${p.descount}%`}
                              size="small"
                              sx={{
                                mt: 0.5,
                                bgcolor: `${themeColors.success}15`,
                                color: themeColors.success,
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={p.status === 1 ? "مفعل" : "معطل"}
                          size="small"
                          icon={p.status === 1 ? <CheckCircleIcon /> : undefined}
                          sx={{
                            bgcolor: p.status === 1 ? `${themeColors.success}15` : `${themeColors.error}15`,
                            color: p.status === 1 ? themeColors.success : themeColors.error,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        {Array.isArray(p.features) && p.features.length > 0 ? (
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Chip
                              label={p.features.length}
                              size="small"
                              sx={{
                                bgcolor: themeColors.secondary.main,
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                            <Tooltip title="عرض الميزات">
                              <IconButton
                                size="small"
                                onClick={() => openFeaturesDialog(p.features)}
                                sx={{
                                  color: themeColors.secondary.main,
                                  "&:hover": { bgcolor: `${themeColors.secondary.main}15` },
                                }}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                          {formatDate(p.created_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={planes.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="عدد الصفوف:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
              sx={{
                borderTop: `1px solid ${themeColors.border.light}`,
                bgcolor: themeColors.background.default,
              }}
            />
          </Paper>
        )}

        {/* Features Dialog */}
        <Dialog
          open={featuresDialogOpen}
          onClose={closeFeaturesDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 2,
              borderBottom: `1px solid ${themeColors.border.light}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <StarsIcon sx={{ color: themeColors.secondary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                تفاصيل الميزات
              </Typography>
              <Chip
                label={dialogFeatures.length}
                size="small"
                sx={{
                  bgcolor: themeColors.secondary.main,
                  color: "white",
                  fontWeight: 600,
                }}
              />
            </Box>
            <IconButton size="small" onClick={closeFeaturesDialog}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {dialogFeatures.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                لا توجد ميزات
              </Alert>
            ) : (
              <List sx={{ p: 0 }}>
                {dialogFeatures.map((f, idx) => (
                  <React.Fragment key={f.id ?? f.pivot?.feature_id}>
                    <ListItem sx={{ px: 0, py: 2, "&:hover": { bgcolor: themeColors.background.hover } }}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <StarsIcon sx={{ color: themeColors.secondary.main, fontSize: 18 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {f.name ?? f.title ?? `feature-${f.id ?? f.pivot?.feature_id}`}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ mt: 1 }}>
                            <Typography variant="caption" display="block">
                              🔑 مفتاح: {f.key ?? "-"}
                            </Typography>
                            <Typography variant="caption" display="block">
                              📊 نوع: {f.type ?? "-"}
                            </Typography>
                            <Typography variant="caption" display="block">
                              💎 قيمة: {f.pivot?.value ?? "-"}
                            </Typography>
                            <Typography variant="caption" display="block">
                              💰 شهري: {f.pivot?.monthly_cost ?? "-"} • سنوي: {f.pivot?.yearly_cost ?? "-"}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < dialogFeatures.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${themeColors.border.light}` }}>
            <Button
              onClick={closeFeaturesDialog}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: themeColors.secondary.main,
                color: themeColors.secondary.main,
              }}
            >
              إغلاق
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default BrokerPlanesPage;
